import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Box, Button, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import { isMSP } from "../../helpers/util_helper";
import { listPartnerPrograms } from "../../helpers/partnerCentral_helper";
import {
  AH_MUTED,
  AH_TEXT,
  AuditHubThemeProvider,
  auditHubPageSx,
  primaryButtonSx,
  tableShellSx,
  toolbarRowSx,
} from "../Compliance/AuditHub/theme";
import ProgramsGrid from "./ProgramsGrid";

const DASHBOARD_PATH = "/partner-central/dashboard";

const Dashboard = () => {
  const history = useHistory();
  const location = useLocation();
  const allowed = isMSP();
  const [programs, setPrograms] = useState([]);
  const [retiredPrograms, setRetiredPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [retiredLoading, setRetiredLoading] = useState(false);
  const [programsError, setProgramsError] = useState("");
  const [retiredError, setRetiredError] = useState("");
  const [programsVersion, setProgramsVersion] = useState(0);

  useEffect(() => {
    document.title = "Dashboard | Partner Central | CloudNova App";
  }, []);

  useEffect(() => {
    const notice = location.state && location.state.notice;
    if (!notice) return;
    enqueueSnackbar(notice, { variant: "success" });
    history.replace(DASHBOARD_PATH);
  }, [location.state, history]);

  useEffect(() => {
    if (!allowed) {
      history.replace("/dashboard");
    }
  }, [allowed, history]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setProgramsLoading(true);
    setRetiredLoading(true);
    setProgramsError("");
    setRetiredError("");

    listPartnerPrograms({ statuses: ["DRAFT", "ACTIVE"] })
      .then((items) => {
        if (!cancelled) setPrograms(items);
      })
      .catch((error) => {
        if (!cancelled) {
          setPrograms([]);
          setProgramsError(error?.message || "Unable to load programs.");
        }
      })
      .finally(() => {
        if (!cancelled) setProgramsLoading(false);
      });

    listPartnerPrograms({ statuses: ["RETIRED"] })
      .then((items) => {
        if (!cancelled) setRetiredPrograms(items);
      })
      .catch((error) => {
        if (!cancelled) {
          setRetiredPrograms([]);
          setRetiredError(error?.message || "Unable to load retired programs.");
        }
      })
      .finally(() => {
        if (!cancelled) setRetiredLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, location.key, programsVersion]);

  if (!allowed) {
    return null;
  }

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={tableShellSx}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="h1"
                    sx={{
                      m: 0,
                      fontWeight: 700,
                      fontSize: { xs: "1.125rem", md: "1.25rem" },
                      color: AH_TEXT,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                    }}
                  >
                    Partner Central
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                    AWS Distributor Dashboard
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...toolbarRowSx,
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    flexWrap: "wrap",
                    overflowX: "visible",
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={() => history.push(`${DASHBOARD_PATH}/add-program`)}
                    sx={{ ...primaryButtonSx, flexShrink: 0 }}
                  >
                    Add Program
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box sx={tableShellSx}>
              <ProgramsGrid
                programs={programs}
                loading={programsLoading}
                error={programsError}
                onRefresh={() => setProgramsVersion((value) => value + 1)}
                title="AWS discount programs"
                emptyMessage="No draft or active programs yet."
                showRetire
                embedded
                tableAriaLabel="Active and draft Partner Central programs"
              />

              <ProgramsGrid
                programs={retiredPrograms}
                loading={retiredLoading}
                error={retiredError}
                onRefresh={() => setProgramsVersion((value) => value + 1)}
                title="Retired Programs"
                emptyMessage="No retired programs yet."
                showRetire={false}
                embedded
                sx={{ mb: 0 }}
                tableAriaLabel="Retired Partner Central programs"
              />
            </Box>
          </Box>
        </Container>
      </div>
    </AuditHubThemeProvider>
  );
};

export default Dashboard;
